import React, { useState, useEffect, useContext, useCallback } from 'react';
import { fmtDate as _fmtDate, fmtDateTime as _fmtDateTime, humanizeDates } from '@/lib/dates';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import LocationImage from '@/components/common/LocationImage';
import SidebarShell from '@/components/wom/SidebarShell';
import ServiceRequestTable from '@/components/wom/ServiceRequestTable';
import ServiceRequestDetail from '@/components/wom/ServiceRequestDetail';
import ServiceRequestForm from '@/components/wom/ServiceRequestForm';
import WorkOrderDetail from '@/components/wom/WorkOrderDetail';
import RequestorManager from '@/components/client/RequestorManager';
import ClientSchedule from '@/components/client/ClientSchedule';
import ClientReports from '@/components/client/ClientReports';
import ClientWorkOrderList from '@/components/client/ClientWorkOrderList';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SR_STATUS, roleLabel } from '@/components/wom/theme';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard, PlusCircle, ClipboardList, CheckSquare, Inbox, ChevronRight,
  CheckCircle2, BadgeCheck, Users, CalendarDays, BarChart3,
  Search, AlertCircle, RefreshCw, FileText, Clock3, MapPin, Building2, CheckCircle,
  ShieldCheck, ArrowLeftRight,
} from 'lucide-react';
import { CLIENT_ROLES } from '@/lib/workspaces';

const PageHead = ({ eyebrow, title, subtitle, action }) => (
  <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
    <div>
      <div className="text-sm font-bold uppercase tracking-[0.18em] text-[#8FC93A]">{eyebrow}</div>
      <h1 className="mt-1.5 text-[40px] leading-tight font-extrabold tracking-tight text-[#0E2A47]">{title}</h1>
      {subtitle && <p className="text-base text-slate-500 mt-2 max-w-2xl">{subtitle}</p>}
    </div>
    {action}
  </div>
);

const Metric = ({ label, value, tone = 'text-[#0E2A47]', onClick, testid }) => (
  <div
    onClick={onClick}
    data-testid={testid}
    className={`rounded-2xl border border-slate-200 bg-white px-6 py-6 transition-all ${onClick ? 'cursor-pointer hover:border-[#8FC93A] hover:shadow-md' : ''}`}
  >
    <div className={`text-5xl font-extrabold tracking-tight ${tone}`}>{value ?? 0}</div>
    <div className="mt-3 text-[15px] font-semibold uppercase tracking-[0.1em] text-slate-500">{label}</div>
  </div>
);

const ClientIdentityBanner = ({ role, customer, token, standalone = false }) => {
  const initials = (customer?.name || '').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '—';
  return (
    <div className={`${standalone ? 'rounded-2xl' : 'rounded-t-2xl'} bg-[#0E2A47] px-5 py-4 sm:px-6 flex items-center gap-4`} data-testid="client-identity-banner">
      <div className="flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-xl bg-white p-1.5 shrink-0">
        {customer?.logo_url
          ? <img src={`${API}/customers/${customer.id}/logo?auth=${token}&v=${(customer.logo_url || '').slice(-16)}`} alt={customer.name} className="h-full w-full object-contain" />
          : <span className="text-xl font-extrabold text-[#0E2A47]">{initials}</span>}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#8FC93A]">{role}</div>
        <div className="text-xl sm:text-2xl font-bold text-white leading-snug tracking-tight truncate">{customer?.name || '—'}</div>
      </div>
    </div>
  );
};

// Persistent location-context banner. Two modes:
//  • 'location' — a single service location (image, name, full address, code)
//  • 'portfolio' — Facilities Director portfolio summary across all locations
const LocationIdentityBanner = ({ mode, location, portfolio, token }) => {
  const shell = 'rounded-b-2xl border border-t-0 border-slate-200 bg-white flex items-center gap-4';
  if (mode === 'portfolio') {
    return (
      <div className={`${shell} px-5 py-4 sm:px-6`} data-testid="location-identity-banner" data-mode="portfolio">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#8FC93A]/12 text-[#5FA718] shrink-0">
          <Building2 size={22} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{portfolio?.label || 'Portfolio Locations'}</div>
          <div className="text-lg font-bold text-[#0E2A47] leading-snug">
            {portfolio?.count || 0} Service Location{portfolio?.count === 1 ? '' : 's'}
          </div>
          {portfolio?.cities && <div className="text-sm text-slate-500 truncate">{portfolio.cities}</div>}
        </div>
      </div>
    );
  }
  const addr = [location?.address, [location?.city, location?.state].filter(Boolean).join(', '), location?.zip].filter(Boolean).join(', ');
  return (
    <div className={`${shell} px-4 py-3 sm:px-5`} data-testid="location-identity-banner" data-mode="location">
      <div className="h-14 w-20 sm:h-16 sm:w-24 shrink-0 overflow-hidden rounded-xl bg-slate-100">
        <LocationImage locationId={location?.id} imageUrl={location?.image_url} name={location?.location_name}
          className="h-full w-full" rounded="rounded-xl" testId="location-banner-image" iconSize={22} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-lg sm:text-xl font-bold text-[#0E2A47] leading-snug truncate">{location?.location_name || '—'}</span>
          {location?.location_code && (
            <span className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-xs font-semibold text-slate-600">#{location.location_code}</span>
          )}
        </div>
        <div className="mt-0.5 flex items-start gap-1.5 text-sm text-slate-500">
          <MapPin size={14} className="mt-0.5 shrink-0 text-slate-400" />
          <span className="truncate">{addr || '—'}</span>
        </div>
      </div>
    </div>
  );
};

const RoutSidebarBrand = ({ customer, token }) => (
  <div className="border-b border-white/10">
    <div className="flex flex-col items-center px-4 pt-7 pb-6 text-center">
      <img src="/app-icons-final/rout_client_v2_256.webp" alt="ROUT by TFS" className="w-[80%] max-w-[190px] h-auto" />
      <div className="mt-4 text-lg font-semibold text-white tracking-tight">Your Facility. On ROUT.</div>
    </div>
  </div>
);

const RoutMobileBrand = () => (
  <img src="/app-icons-final/rout_client_v2_256.webp" alt="ROUT by TFS" className="h-12 w-auto shrink-0" />
);

const ClientPortal = () => {
  const { token, user } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const isClientRole = CLIENT_ROLES.includes(user?.role);
  const actingOnBehalf = !isClientRole;
  // Administrator Client View: internal user acting on behalf of a selected customer.
  const [obCustomerId, setObCustomerId] = useState(null);
  const [obCustomers, setObCustomers] = useState([]);
  // Genuine Facilities Director OR an internal admin acting on behalf → director-style oversight.
  const isDirector = actingOnBehalf ? true : (user?.role === 'client_facilities_director');
  // Genuine Facilities Director self-service request creation (neutral routing).
  const fdMode = isDirector && !actingOnBehalf;

  const [view, setView] = useState('dashboard');
  const [selectedId, setSelectedId] = useState(null);
  const [stats, setStats] = useState({});
  const [items, setItems] = useState([]);
  const [listStatus, setListStatus] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [customer, setCustomer] = useState(null);
  const [woItems, setWoItems] = useState([]);
  const [woLoading, setWoLoading] = useState(false);
  const [woError, setWoError] = useState(false);
  const [completedSearch, setCompletedSearch] = useState('');
  const [locations, setLocations] = useState([]);
  const [allWos, setAllWos] = useState([]);
  const [woListFilter, setWoListFilter] = useState({});
  const [selectedWoId, setSelectedWoId] = useState(null);
  const [woReturn, setWoReturn] = useState('completed-services');
  const [selectedLocationId, setSelectedLocationId] = useState(null);

  useEffect(() => {
    if (!isDirector && locations.length && !selectedLocationId) setSelectedLocationId(locations[0].id);
  }, [locations, isDirector, selectedLocationId]);

  const loadWorkOrders = useCallback(async () => {
    setWoLoading(true); setWoError(false);
    try {
      const r = await axios.get(`${API}/work-orders`, { ...authHeaders, params: { status_filter: 'Completed' } });
      setWoItems(r.data || []);
    } catch (e) { setWoError(true); }
    finally { setWoLoading(false); }
  }, []);

  const loadStats = useCallback(async () => {
    // Administrator Client View: no customer selected yet → show nothing (no global leak)
    if (actingOnBehalf && !obCustomerId) { setStats({}); return; }
    try {
      const params = actingOnBehalf
        ? (obCustomerId ? { customer_id: obCustomerId } : {})
        : ((!isDirector && selectedLocationId) ? { location_id: selectedLocationId } : {});
      const r = await axios.get(`${API}/service-requests/stats/operations`, { ...authHeaders, params });
      setStats(r.data || {});
    } catch (e) { /* noop */ }
  }, [isDirector, selectedLocationId, actingOnBehalf, obCustomerId]);

  useEffect(() => {
    const params = actingOnBehalf ? (obCustomerId ? { customer_id: obCustomerId } : {}) : {};
    axios.get(`${API}/service-requests/context/client`, { ...authHeaders, params })
      .then((r) => {
        setCustomerName(r.data?.customer?.name || '');
        setCustomer(r.data?.customer || null);
        if (actingOnBehalf) {
          setObCustomers(r.data?.customers || []);
          setLocations(r.data?.locations || []);
        }
      })
      .catch(() => {});
    if (!actingOnBehalf) {
      axios.get(`${API}/service-locations`, authHeaders)
        .then((r) => setLocations(r.data || []))
        .catch(() => {});
    }
  }, [actingOnBehalf, obCustomerId]);

  const loadList = useCallback(async (status = null) => {
    const params = status ? { status_filter: status } : {};
    const r = await axios.get(`${API}/service-requests`, { ...authHeaders, params });
    setItems(r.data || []);
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => {
    if (['my-requests', 'all-requests', 'approvals'].includes(view)) {
      loadList(view === 'approvals' ? SR_STATUS.SUBMITTED : listStatus);
    }
    if (view === 'completed-services') loadWorkOrders();
    if (view === 'wo-list') {
      axios.get(`${API}/work-orders`, authHeaders).then((r) => setAllWos(r.data || [])).catch(() => {});
    }
  }, [view, listStatus, loadList, loadWorkOrders]);

  const openDetail = (id) => { setSelectedId(id); setView('detail'); };
  const openWoDetail = (id, returnView = 'completed-services') => { setSelectedWoId(id); setWoReturn(returnView); setView('wo-detail'); };
  const openWoList = (filter) => { setWoListFilter(filter || {}); setView('wo-list'); };

  const navItems = actingOnBehalf
    ? [
        { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { key: 'new', label: 'New Request (On Behalf)', icon: PlusCircle },
        { key: 'approvals', label: 'Approvals', icon: CheckSquare, badge: stats.new_requests },
        { key: 'all-requests', label: 'All Requests', icon: ClipboardList },
        { key: 'completed-services', label: 'Completed Services', icon: CheckCircle2 },
      ]
    : isDirector
    ? [
        { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { key: 'new', label: 'New Request', icon: PlusCircle },
        { key: 'approvals', label: 'Approvals', icon: CheckSquare, badge: stats.new_requests },
        { key: 'all-requests', label: 'All Requests', icon: ClipboardList },
        { key: 'schedule', label: 'Schedule', icon: CalendarDays },
        { key: 'reports', label: 'Reports', icon: BarChart3 },
        { key: 'requestors', label: 'Location Requestors', icon: Users },
        { key: 'completed-services', label: 'Completed Services', icon: CheckCircle2 },
      ]
    : [
        { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { key: 'new', label: 'New Request', icon: PlusCircle },
        { key: 'my-requests', label: 'My Requests', icon: ClipboardList },
        { key: 'completed-services', label: 'Completed Services', icon: CheckCircle2 },
      ];

  const activeKey = view === 'detail'
    ? (isDirector ? 'all-requests' : 'my-requests')
    : view === 'wo-list' ? 'reports'
    : view === 'wo-detail' ? woReturn : view;

  const newRequestBtn = !isDirector && (
    <Button className="h-12 px-6 text-base rounded-xl bg-[#0E2A47] hover:bg-[#16324F]" onClick={() => setView('new')} data-testid="client-new-request-btn">
      <PlusCircle size={18} className="mr-2" /> New Request
    </Button>
  );

  // Persistent location context for the shared identity header
  const resolveLoc = (name) => {
    const key = (name || '').trim().toLowerCase();
    return locations.find((l) => (l.location_name || '').trim().toLowerCase() === key) || null;
  };
  const recordLoc = (() => {
    if (view === 'detail') {
      const r = items.find((i) => i.id === selectedId);
      return r ? resolveLoc(r.location_name || r.service_location) : null;
    }
    if (view === 'wo-detail') {
      const r = [...woItems, ...allWos].find((w) => w.id === selectedWoId);
      return r ? resolveLoc(r.service_location) : null;
    }
    return null;
  })();
  const selectedLocation = locations.find((l) => l.id === selectedLocationId) || locations[0] || null;
  const portfolioCities = (() => {
    // Portfolio preview must be LOCATION-based so the "+N more" math matches the
    // authoritative location count (22 → 4 shown + "+18 more"), not the city count.
    const names = [...new Set(locations.map((l) => l.location_name).filter(Boolean))];
    return names.length ? (names.length > 4 ? `${names.slice(0, 4).join(' · ')} +${names.length - 4} more` : names.join(' · ')) : '';
  })();
  const isRecordView = view === 'detail' || view === 'wo-detail';
  const requestorAll = !isDirector && !selectedLocationId;
  const locBanner = (isRecordView && recordLoc)
    ? { mode: 'location', location: recordLoc }
    : (isDirector || requestorAll)
      ? { mode: 'portfolio' }
      : { mode: 'location', location: selectedLocation };

  // Administrator Client View: scope all lists to the selected customer's locations.
  const obLocIds = React.useMemo(() => new Set(locations.map((l) => l.id)), [locations]);
  const obLocNames = React.useMemo(() => new Set(locations.map((l) => (l.location_name || '').trim().toLowerCase())), [locations]);
  const scopeOB = (list) => (actingOnBehalf
    ? (list || []).filter((x) => obLocIds.has(x.location_id) || obLocNames.has((x.service_location || x.location_name || '').trim().toLowerCase()))
    : (list || []));

  // Location-filtered data for the active-location context (requester)
  const activeLocName = (selectedLocation?.location_name || '').trim().toLowerCase();
  const itemsForActive = scopeOB((!isDirector && selectedLocationId)
    ? items.filter((i) => i.location_id === selectedLocationId)
    : items);
  const woItemsForActive = scopeOB((!isDirector && selectedLocationId)
    ? woItems.filter((w) => w.location_id === selectedLocationId || (w.service_location || '').trim().toLowerCase() === activeLocName)
    : woItems);

  const ActiveLocationBar = () => {
    if (isDirector || locations.length === 0) return null;
    return (
      <div className="mb-6 flex flex-wrap items-center gap-3" data-testid="active-location-bar">
        <span className="inline-flex items-center gap-2 rounded-full bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white">
          <MapPin size={15} className="text-[#8FC93A]" />
          Viewing: {selectedLocationId
            ? <>{selectedLocation?.location_name}{selectedLocation?.location_code ? <span className="ml-1 font-mono text-[#8FC93A]">· {selectedLocation.location_code}</span> : null}</>
            : 'All Authorized Locations'}
        </span>
        {locations.length > 1 && (
          <Select value={selectedLocationId || '__all__'} onValueChange={(v) => setSelectedLocationId(v === '__all__' ? null : v)}>
            <SelectTrigger className="h-9 w-[240px] rounded-full text-sm" data-testid="active-location-switch"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Authorized Locations</SelectItem>
              {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.location_name}{l.location_code ? ` · ${l.location_code}` : ''}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
    );
  };

  return (
    <SidebarShell
      navItems={navItems} activeKey={activeKey}
      onNavigate={(k) => { setSelectedId(null); setListStatus(null); setView(k); }}
      brandNode={<RoutSidebarBrand customer={customer} token={token} />}
      mobileBrand={<RoutMobileBrand />}
      logoutTo={actingOnBehalf ? '/login' : '/rout'}
      showPortalHome={actingOnBehalf}
      wide
      navAccent="#C8961E"
      clockMode="local"
      workspaceLabel={actingOnBehalf ? 'Administrator Client View' : `${roleLabel(user?.role)} Workspace`}
    >
      {/* ROUT Client Portal banner — ALWAYS at the very top of every client view
          (dashboard, lists, and the admin customer-select gate), identical treatment
          to every other module: object-contain artwork right, heading over white left. */}
      <div className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="client-overview-banner">
        <div className="relative h-[118px] sm:h-[148px]">
          <img src="/module-banners/rout-client-banner.webp" alt="" aria-hidden="true"
            className="absolute right-0 top-0 h-full w-auto max-w-none object-contain object-right" data-testid="client-banner-img" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, #ffffff 0%, #ffffff 30%, rgba(255,255,255,0.72) 46%, rgba(255,255,255,0) 66%)' }} aria-hidden="true" />
          <div className="absolute inset-0 sm:hidden" style={{ background: 'linear-gradient(90deg, #ffffff 0%, #ffffff 50%, rgba(255,255,255,0.82) 63%, rgba(255,255,255,0) 83%)' }} aria-hidden="true" />
          <div className="relative z-10 flex h-full flex-col justify-center px-6">
            <h1 className="text-[24px] sm:text-[32px] leading-tight font-extrabold tracking-tight text-[#0E2A47]" data-testid="client-overview-title">Work Order MGT</h1>
            <p className="mt-0.5 text-[13px] sm:text-sm font-medium text-slate-500" data-testid="client-overview-sub">Client Interface</p>
          </div>
        </div>
      </div>

      {/* Administrator Client View — customer/location context selection gate */}
      {actingOnBehalf && !obCustomerId ? (
        <div className="mx-auto max-w-2xl" data-testid="admin-client-view-gate">
          <div className="rounded-2xl border border-amber-300 bg-amber-50 px-6 py-5 mb-6">
            <div className="flex items-center gap-3">
              <ShieldCheck size={22} className="text-amber-700" />
              <div>
                <div className="text-sm font-bold uppercase tracking-[0.15em] text-amber-700">Administrator Client View — Acting on Behalf</div>
                <p className="mt-1 text-sm text-slate-600">
                  Select a customer to enter the client portal on their behalf. Your administrator ID is recorded on any
                  request created here. No customer is selected by default.
                </p>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <label className="text-sm font-semibold text-[#0E2A47]">Customer</label>
            <Select value={obCustomerId || ''} onValueChange={(v) => { setSelectedLocationId(null); setListStatus(null); setSelectedId(null); setObCustomerId(v); }}>
              <SelectTrigger className="mt-2 h-12 rounded-xl" data-testid="admin-client-view-customer-select">
                <SelectValue placeholder="Select a customer…" />
              </SelectTrigger>
              <SelectContent>
                {obCustomers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="mt-3 text-xs text-slate-400">Request creation stays disabled until a customer and service location are chosen.</p>
          </div>
        </div>
      ) : (
      <>
      {actingOnBehalf && (
        view === 'dashboard' ? (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border-2 border-amber-300 bg-amber-50 px-6 py-5" data-testid="admin-client-view-banner">
            <div className="flex items-center gap-4">
              {customer?.logo_url
                ? <img src={`${API}/customers/${customer.id}/logo?auth=${token}`} alt={customer?.name} onError={(e) => { e.currentTarget.style.display = 'none'; }} className="h-14 w-14 rounded-xl bg-white object-contain p-1 ring-1 ring-amber-200" />
                : <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-white ring-1 ring-amber-200"><Building2 className="text-slate-300" size={26} /></div>}
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-700">Administrator Client View — Active Customer</div>
                <div className="mt-0.5 text-2xl font-extrabold text-[#0E2A47]">{customer?.name || '—'}</div>
              </div>
            </div>
            <button onClick={() => { setSelectedLocationId(null); setListStatus(null); setSelectedId(null); setObCustomerId(null); setView('dashboard'); }} data-testid="admin-client-view-change-customer"
              className="inline-flex items-center gap-2 rounded-xl bg-[#0E2A47] px-5 py-3 text-sm font-bold text-white shadow-sm transition-colors hover:bg-[#16324F]">
              <ArrowLeftRight size={16} /> Change Customer
            </button>
          </div>
        ) : (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-5 py-3" data-testid="admin-client-view-banner">
            <div className="flex items-center gap-2.5 text-sm">
              {customer?.logo_url
                ? <img src={`${API}/customers/${customer.id}/logo?auth=${token}`} alt={customer?.name} onError={(e) => { e.currentTarget.style.display = 'none'; }} className="h-8 w-8 rounded bg-white object-contain p-0.5 ring-1 ring-amber-200" />
                : <ShieldCheck size={18} className="text-amber-700" />}
              <span className="font-bold uppercase tracking-wide text-amber-700">Active Customer:</span>
              <span className="font-semibold text-[#0E2A47]">{customer?.name || '—'}</span>
            </div>
            <button onClick={() => setView('dashboard')} data-testid="admin-client-view-change-customer"
              className="inline-flex items-center gap-1.5 rounded-full border border-amber-400 bg-white px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100">
              <LayoutDashboard size={13} /> Return to Dashboard to change customer
            </button>
          </div>
        )
      )}
      {/* Persistent shared identity header — customer (global) + location context */}
      <div className="mb-8" data-testid="client-identity-header">
        <ClientIdentityBanner role={roleLabel(user?.role)} customer={customer} token={token} standalone={view === 'new'} />
        {view !== 'new' && (locBanner.mode === 'portfolio'
          ? <LocationIdentityBanner mode="portfolio" portfolio={{ count: locations.length, cities: portfolioCities, label: requestorAll ? 'All Authorized Locations' : 'Portfolio Locations' }} />
          : <LocationIdentityBanner mode="location" location={locBanner.location} token={token} />)}
      </div>

      {view === 'dashboard' && (
        <div className="space-y-8" data-testid="client-dashboard">
          {!isDirector && <ActiveLocationBar />}
          {/* Executive ROUT hero */}
          <div className="relative overflow-hidden rounded-3xl bg-[#0E2A47] px-8 py-10 lg:px-10 lg:py-12 text-white" data-testid="client-customer-identity">
            <svg className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.07]" preserveAspectRatio="none" viewBox="0 0 1000 300" aria-hidden="true">
              <path d="M-20 240 C 200 160, 320 260, 520 170 S 820 60, 1040 120" stroke="#8FC93A" strokeWidth="2" strokeDasharray="6 12" fill="none" />
              <path d="M-20 120 C 220 60, 360 150, 600 70 S 900 20, 1040 60" stroke="#ffffff" strokeWidth="2" strokeDasharray="10 14" fill="none" />
            </svg>
            <div className="relative flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h1 className="flex flex-wrap items-baseline gap-x-3 font-extrabold tracking-tight leading-tight">
                  <span className="text-2xl lg:text-3xl font-semibold text-[#8FC93A]">Welcome,</span>
                  <span className="text-4xl lg:text-5xl text-white">{user?.name ? user.name.split(' ')[0] : ''}</span>
                </h1>
                <p className="mt-3 text-base text-slate-400 max-w-xl">
                  {isDirector ? 'Review and approve maintenance requests across your portfolio.' : 'Submit and track your maintenance requests.'}
                </p>
              </div>
              {!isDirector && (
                <Button className="h-12 px-6 text-base bg-[#8FC93A] text-[#0E2A47] hover:bg-[#7fb82f] font-semibold shadow-sm shrink-0 rounded-xl" onClick={() => setView('new')} data-testid="client-dashboard-new-btn">
                  <PlusCircle size={18} className="mr-2" /> New Service Request
                </Button>
              )}
              {fdMode && (
                <Button className="h-12 px-6 text-base bg-[#8FC93A] text-[#0E2A47] hover:bg-[#7fb82f] font-semibold shadow-sm shrink-0 rounded-xl" onClick={() => setView('new')} data-testid="fd-dashboard-new-btn">
                  <PlusCircle size={18} className="mr-2" /> New Request
                </Button>
              )}
            </div>
          </div>

          {/* Location Requestor — assigned location(s) */}
          {!isDirector && locations.length > 0 && (
            <div data-testid="requestor-locations">
              <div className="mb-4 text-sm font-bold uppercase tracking-[0.18em] text-[#8FC93A]">
                Your Service Location{locations.length > 1 ? 's' : ''}
                {locations.length > 1 && <span className="ml-2 text-slate-400 normal-case tracking-normal font-medium">— select one to set your active location</span>}
              </div>
              <div className={`grid gap-6 ${locations.length > 1 ? 'md:grid-cols-2' : ''}`}>
                {locations.map((loc) => {
                  const addr = [loc.address, [loc.city, loc.state].filter(Boolean).join(', '), loc.zip].filter(Boolean).join(', ');
                  const selected = loc.id === selectedLocationId;
                  return (
                    <button
                      type="button"
                      key={loc.id}
                      onClick={() => setSelectedLocationId(loc.id)}
                      className={`group text-left overflow-hidden rounded-2xl border bg-white shadow-sm transition-all hover:shadow-md ${selected ? 'border-[#8FC93A] ring-2 ring-[#8FC93A]/40' : 'border-slate-200 hover:border-[#8FC93A]/50'}`}
                      data-testid={`requestor-location-${loc.id}`}
                      data-selected={selected}
                    >
                      <div className="relative h-56 w-full bg-slate-100">
                        <LocationImage locationId={loc.id} imageUrl={loc.image_url} name={loc.location_name}
                          className="h-full w-full" rounded="rounded-none" testId={`requestor-loc-image-${loc.id}`} iconSize={30} />
                        {selected && (
                          <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-[#8FC93A] px-3 py-1 text-xs font-bold uppercase tracking-wide text-[#0E2A47] shadow">
                            <CheckCircle size={14} /> Active
                          </span>
                        )}
                      </div>
                      <div className="p-6">
                        <div className="text-sm font-semibold uppercase tracking-wide text-[#8FC93A]">{customerName || loc.customer_name}</div>
                        <div className="mt-1 text-2xl font-bold text-[#0E2A47] leading-snug">{loc.location_name}</div>
                        <div className="mt-2 flex items-start gap-2 text-base text-slate-600">
                          <MapPin size={18} className="mt-0.5 shrink-0 text-slate-400" />
                          <span>{addr || '—'}</span>
                        </div>
                        {loc.location_code && (
                          <div className="mt-2 text-sm text-slate-400">Location #<span className="font-mono font-semibold text-slate-600">{loc.location_code}</span></div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {isDirector && <Metric testid="client-metric-pending" label="Awaiting Your Approval" value={stats.new_requests} tone="text-[#C8641E]" onClick={() => setView('approvals')} />}
            <Metric testid="client-metric-approved" label="Approved" value={stats.awaiting_wo_processing} tone="text-[#5FA718]" />
            <Metric testid="client-metric-info" label="Info Requested" value={stats.information_requested} tone="text-[#0E2A47]" />
            <Metric testid="client-metric-total" label="Total Requests" value={stats.total_requests} tone="text-[#0E2A47]" />
          </div>
        </div>
      )}

      {view === 'new' && (
        <div className="max-w-3xl rout-scale">
          <ServiceRequestForm mode={actingOnBehalf ? 'proxy' : 'client'} activeLocationId={fdMode ? null : selectedLocationId}
            lockedCustomer={actingOnBehalf ? customer : null} fdMode={fdMode}
            onSubmitted={() => { loadStats(); setView(isDirector ? 'all-requests' : 'my-requests'); }}
            onCancel={() => setView('dashboard')} />
        </div>
      )}

      {view === 'my-requests' && (
        <div className="rout-scale">
          <PageHead eyebrow="Location Requestor" title="My Requests" subtitle="Track the status of requests you've submitted." action={newRequestBtn} />
          <ActiveLocationBar />
          <ServiceRequestTable requests={itemsForActive} onSelect={openDetail} emptyLabel="You haven't submitted any requests yet." />
        </div>
      )}

      {view === 'approvals' && (
        <div className="rout-scale">
          <PageHead eyebrow={actingOnBehalf ? 'Administrator Client View' : 'Facilities Director'} title="Approvals" subtitle="Requests awaiting your review." />
          <ServiceRequestTable requests={scopeOB(items)} onSelect={openDetail} emptyLabel="No requests awaiting approval." />
        </div>
      )}

      {view === 'all-requests' && (
        <div className="rout-scale">
          <PageHead eyebrow={actingOnBehalf ? 'Administrator Client View' : 'Facilities Director'} title="All Requests" subtitle="Portfolio-wide request visibility." />
          <ServiceRequestTable requests={scopeOB(items)} onSelect={openDetail} emptyLabel="No requests found." />
        </div>
      )}

      {view === 'requestors' && isDirector && <RequestorManager />}

      {view === 'schedule' && isDirector && (
        <div className="rout-scale"><ClientSchedule onOpen={(id) => openWoDetail(id, 'schedule')} /></div>
      )}

      {view === 'reports' && isDirector && <div className="rout-scale"><ClientReports onDrill={(f) => openWoList(f)} /></div>}

      {view === 'wo-list' && isDirector && (
        <div className="rout-scale"><ClientWorkOrderList allWos={allWos} filter={woListFilter} setFilter={setWoListFilter}
          onOpen={(id) => openWoDetail(id, 'wo-list')} onBack={() => setView('reports')} /></div>
      )}

      {view === 'completed-services' && (
        <div data-testid="client-completed-services">
          <PageHead eyebrow={isDirector ? 'Facilities Director' : 'Location Requestor'} title="Completed Services"
            subtitle="Your completed work with downloadable ROUT completion reports for your records." />
          {!isDirector && <ActiveLocationBar />}

          {(() => {
            const q = completedSearch.trim().toLowerCase();
            const base = woItemsForActive;
            const filtered = q
              ? base.filter((w) =>
                  [w.job_name, w.tfs_work_order_number, w.service_location, w.area_name, w.trade_type]
                    .filter(Boolean).some((v) => String(v).toLowerCase().includes(q)))
              : base;
            const reportStatusOf = (w) => {
              const delivered = !!(w.customer_notified && (w.customer_notified_at || w.customer_notification_status === 'Sent'));
              if (delivered) return 'Delivered';
              if (w.completion_report_generated_at) return 'Available';
              return 'Pending';
            };
            const availableCount = base.filter((w) => reportStatusOf(w) !== 'Pending').length;
            const locOf = (w) => {
              const key = (w.service_location || '').trim().toLowerCase();
              return locations.find((l) => (l.location_name || '').trim().toLowerCase() === key) || {};
            };
            const fmtDT = (d) => {
              if (!d) return { date: '—', time: '' };
              const full = _fmtDateTime(d);
              return { date: _fmtDate(d) || '—', time: full ? full.slice(full.indexOf(' ') + 1) : '' };
            };
            const StatusBadge = ({ w }) => {
              const s = reportStatusOf(w);
              const map = {
                Delivered: 'bg-emerald-50 text-emerald-700',
                Available: 'bg-sky-50 text-sky-700',
                Pending: 'bg-amber-50 text-amber-700',
              };
              return (
                <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[14px] font-semibold ${map[s]}`}>
                  {s === 'Delivered' ? <BadgeCheck size={15} /> : s === 'Available' ? <FileText size={14} /> : <Clock3 size={14} />}
                  {s}
                </span>
              );
            };

            return (
              <div className="space-y-6">
                {/* Summary */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
                  <div className="rounded-2xl border border-slate-200 bg-white px-6 py-5" data-testid="completed-summary-total">
                    <div className="text-4xl font-extrabold text-[#0E2A47]">{base.length}</div>
                    <div className="mt-1.5 text-sm font-semibold uppercase tracking-[0.12em] text-slate-400">Completed Services</div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white px-6 py-5" data-testid="completed-summary-reports">
                    <div className="text-4xl font-extrabold text-[#5FA718]">{availableCount}</div>
                    <div className="mt-1.5 text-[15px] font-semibold uppercase tracking-[0.1em] text-slate-500">Completion Reports Available</div>
                  </div>
                </div>

                {/* Search */}
                {base.length > 0 && (
                  <div className="relative max-w-md">
                    <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      data-testid="completed-search-input"
                      value={completedSearch}
                      onChange={(e) => setCompletedSearch(e.target.value)}
                      placeholder="Search by job, WO #, location or trade…"
                      className="w-full rounded-xl border border-slate-200 bg-white pl-11 pr-4 h-12 text-base text-slate-700 placeholder:text-slate-400 focus:border-[#8FC93A] focus:outline-none focus:ring-2 focus:ring-[#8FC93A]/20"
                    />
                  </div>
                )}

                {/* List / states */}
                {woLoading ? (
                  <Card className="border-slate-200 shadow-sm rounded-2xl">
                    <CardContent className="p-0 divide-y divide-slate-100" data-testid="completed-loading">
                      {[0, 1, 2].map((i) => (
                        <div key={i} className="flex items-center gap-3 px-5 py-4 animate-pulse">
                          <div className="h-9 w-9 rounded-lg bg-slate-100" />
                          <div className="flex-1 space-y-2"><div className="h-3.5 w-1/3 rounded bg-slate-100" /><div className="h-3 w-1/2 rounded bg-slate-50" /></div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                ) : woError ? (
                  <Card className="border-slate-200 shadow-sm rounded-2xl">
                    <CardContent className="py-16 text-center" data-testid="completed-error">
                      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-400"><AlertCircle size={26} /></div>
                      <p className="text-sm text-slate-500 mb-4">We couldn't load your completed services.</p>
                      <Button variant="outline" onClick={loadWorkOrders} data-testid="completed-retry-btn">
                        <RefreshCw size={14} className="mr-1.5" /> Try again
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="border-slate-200 shadow-sm rounded-2xl overflow-hidden">
                    <CardContent className="p-0">
                      {base.length === 0 ? (
                        <div className="py-16 text-center" data-testid="completed-empty">
                          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-50 text-slate-300"><Inbox size={28} /></div>
                          <p className="text-base text-slate-400">No completed services yet. Reports appear here once work is completed.</p>
                        </div>
                      ) : filtered.length === 0 ? (
                        <div className="py-16 text-center text-base text-slate-400" data-testid="completed-no-match">
                          No completed services match “{completedSearch}”.
                        </div>
                      ) : (
                        <>
                          {/* Desktop / laptop table */}
                          <div className="hidden lg:block overflow-x-auto">
                            <table className="w-full text-left" data-testid="completed-table">
                              <thead>
                                <tr className="border-b border-slate-200 bg-slate-50/70 text-[15px] font-bold uppercase tracking-wide text-slate-500">
                                  <th className="px-6 py-4">Location #</th>
                                  <th className="px-6 py-4">Location</th>
                                  <th className="px-6 py-4">Service Completed</th>
                                  <th className="px-6 py-4">Completed On</th>
                                  <th className="px-6 py-4">Report Status</th>
                                  <th className="px-6 py-4 text-right">View / Download</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {filtered.map((w) => {
                                  const loc = locOf(w);
                                  const dt = fmtDT(w.completed_at);
                                  const addr = [loc.address, [loc.city, loc.state].filter(Boolean).join(', '), loc.zip].filter(Boolean).join(' · ');
                                  return (
                                    <tr key={w.id} className="align-top transition-colors hover:bg-[#8FC93A]/[0.05]" data-testid={`client-wo-row-${w.id}`}>
                                      <td className="px-6 py-5">
                                        <span className="font-mono text-[16px] font-semibold text-[#0E2A47]">{loc.location_code || loc.account_number || '—'}</span>
                                      </td>
                                      <td className="px-6 py-5">
                                        <div className="text-[17px] font-semibold text-slate-800">{w.service_location || '—'}</div>
                                        {addr && <div className="text-[15px] text-slate-500 mt-1">{addr}</div>}
                                        {w.area_name && <div className="text-[14px] text-slate-400 mt-0.5">Area: {w.area_name}</div>}
                                      </td>
                                      <td className="px-6 py-5">
                                        <div className="text-[16px] font-medium text-slate-700">{humanizeDates(w.job_name) || '—'}</div>
                                        <div className="text-[14px] text-slate-400 mt-0.5">
                                          <span className="font-mono">{w.tfs_work_order_number}</span>{w.trade_type ? ` · ${w.trade_type}` : ''}
                                        </div>
                                      </td>
                                      <td className="px-6 py-5">
                                        <div className="text-[16px] font-semibold text-slate-700">{dt.date}</div>
                                        {dt.time && <div className="text-[14px] text-slate-400 mt-0.5">{dt.time}</div>}
                                      </td>
                                      <td className="px-6 py-5"><StatusBadge w={w} /></td>
                                      <td className="px-6 py-5 text-right">
                                        <Button onClick={() => openWoDetail(w.id)} data-testid={`client-wo-view-${w.id}`}
                                          className="h-11 px-5 text-base rounded-xl bg-[#0E2A47] hover:bg-[#16324F]">
                                          View
                                        </Button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>

                          {/* Mobile stacked cards */}
                          <div className="lg:hidden divide-y divide-slate-100" data-testid="completed-cards-mobile">
                            {filtered.map((w) => {
                              const loc = locOf(w);
                              const dt = fmtDT(w.completed_at);
                              const addr = [loc.address, [loc.city, loc.state].filter(Boolean).join(', '), loc.zip].filter(Boolean).join(', ');
                              return (
                                <div key={w.id} className="p-5" data-testid={`client-wo-card-${w.id}`}>
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <div className="text-[13px] font-semibold uppercase tracking-wide text-slate-400">
                                        Location #{loc.location_code || loc.account_number || '—'}
                                      </div>
                                      <div className="text-[18px] font-bold text-slate-800 leading-snug mt-0.5">{w.service_location || '—'}</div>
                                    </div>
                                    <StatusBadge w={w} />
                                  </div>

                                  {addr && (
                                    <div className="mt-2 flex items-start gap-1.5 text-[15px] text-slate-500">
                                      <MapPin size={15} className="mt-0.5 shrink-0 text-slate-400" />
                                      <span>{addr}</span>
                                    </div>
                                  )}
                                  {w.area_name && <div className="mt-1 text-[15px] text-slate-500">Area/Suite: <span className="font-medium text-slate-600">{w.area_name}</span></div>}

                                  <div className="mt-4 rounded-xl bg-slate-50 p-4">
                                    <div className="text-[18px] font-semibold text-slate-800 leading-snug">{humanizeDates(w.job_name) || '—'}</div>
                                    <div className="mt-1 text-[15px] text-slate-500">
                                      <span className="font-mono font-semibold text-[#0E2A47]">{w.tfs_work_order_number}</span>
                                      {w.trade_type ? ` · ${w.trade_type}` : ''}
                                    </div>
                                    <div className="mt-2 text-[15px] text-slate-500">
                                      Completed <span className="font-semibold text-slate-700">{dt.date}</span>{dt.time ? ` · ${dt.time}` : ''}
                                    </div>
                                  </div>

                                  <Button onClick={() => openWoDetail(w.id)} data-testid={`client-wo-view-${w.id}`}
                                    className="mt-4 w-full h-12 text-base rounded-xl bg-[#0E2A47] hover:bg-[#16324F]">
                                    <FileText size={18} className="mr-2" /> View / Download Report
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {view === 'wo-detail' && (
        <div className="rout-scale"><WorkOrderDetail workOrderId={selectedWoId} canManage={false}
          onBack={() => setView(woReturn)} /></div>
      )}

      {view === 'detail' && (
        <div className="rout-scale"><ServiceRequestDetail requestId={selectedId} canReview={isDirector}
          onBack={() => setView(isDirector ? 'all-requests' : 'my-requests')}
          onChanged={() => loadStats()} /></div>
      )}
      </>
      )}
    </SidebarShell>
  );
};

export default ClientPortal;
